// Interní registr agend a duplicitní-ID ochrana

import type { StoredAgendaConfig } from './agendaDefinition';
import type { ParamCodecMap } from './paramCodec';

/** Vstupní typ pro registraci – přijímá jakýkoli kompatibilní config */
interface RegistrableAgendaConfig {
  readonly id: string;
  readonly path: string;
  readonly open: ParamCodecMap;
  readonly state?: ParamCodecMap;
  readonly title?: string | ((...args: never[]) => string);
  readonly presentationHints?: {
    readonly width?: string;
    readonly closable?: boolean;
    readonly windowKind?: string;
    readonly shell?: {
      readonly layoutMode: string;
      readonly detailAgendaAllowlist?: readonly string[];
    };
  };
}

/**
 * Interní registr agend:
 * - byAgendaId: O(1) lookup podle agendaId
 * - byPath: O(1) lookup podle path pro restore z URL
 */
export class AgendaRegistry {
  private readonly byAgendaId = new Map<string, StoredAgendaConfig>();
  private readonly byPath = new Map<string, StoredAgendaConfig>();

  register(config: RegistrableAgendaConfig): void {
    if (this.byAgendaId.has(config.id)) {
      throw new Error(
        `Agenda "${config.id}" is already registered. Duplicate registration is not allowed.`
      );
    }

    if (this.byPath.has(config.path)) {
      throw new Error(
        `Path "${config.path}" is already registered by agenda "${this.byPath.get(config.path)?.id}". ` +
          `Agenda "${config.id}" cannot use the same path.`
      );
    }

    this.byAgendaId.set(config.id, config as StoredAgendaConfig);
    this.byPath.set(config.path, config as StoredAgendaConfig);
  }

  getById(agendaId: string): StoredAgendaConfig | undefined {
    return this.byAgendaId.get(agendaId);
  }

  getByPath(path: string): StoredAgendaConfig | undefined {
    return this.byPath.get(path);
  }

  has(agendaId: string): boolean {
    return this.byAgendaId.has(agendaId);
  }

  get size(): number {
    return this.byAgendaId.size;
  }

  getAllIds(): string[] {
    return [...this.byAgendaId.keys()];
  }
}
